// ─── PrivacyPage ─────────────────────────────────────────────────────────────
// DPDPA 2023-compliant Privacy Policy placeholder.
// IMPORTANT: This document contains placeholder content. Have it reviewed and
// finalised by a qualified Indian data protection lawyer before enforcement.

export function PrivacyPage() {
  return (
    <div className="min-h-svh px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-12">

        {/* ── Header ── */}
        <div className="space-y-4">
          <a href="/" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            ← Back to SafetyQR
          </a>
          <p className="shell-kicker text-xs font-semibold tracking-[0.18em] uppercase text-primary">
            Legal
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground leading-6">
            Last updated: March 2026 &nbsp;·&nbsp; Effective date: March 2026
          </p>
          <div className="rounded-3xl border border-amber-300/70 bg-amber-50 px-5 py-4 text-sm leading-6 text-amber-900">
            <strong>Note:</strong> This Privacy Policy is a structured placeholder prepared
            in accordance with India's Digital Personal Data Protection Act (DPDPA) 2023.
            It is currently under legal review and will be updated with finalised language
            before full enforcement. If you have questions, contact us at{" "}
            <a href="mailto:support@feedbacknfc.com" className="underline">
              support@feedbacknfc.com
            </a>.
          </div>
        </div>

        <Divider />

        {/* ── 1. Who We Are ── */}
        <Section number="1" title="Who We Are">
          <p>
            SafetyQR is a product of <strong>FeedbackNFC</strong> ("we", "our", "us"), a
            platform that provides QR sticker-based safety kits for individuals, students,
            women, elderly persons, vehicle owners, and plantation site operators across
            India. Our service allows a person scanning your QR sticker to alert your
            emergency contacts and share your location in an emergency.
          </p>
          <p className="mt-3">
            <strong>Contact:</strong><br />
            FeedbackNFC / SafetyQR<br />
            Email:{" "}
            <a href="mailto:support@feedbacknfc.com" className="text-primary hover:underline">
              support@feedbacknfc.com
            </a>
          </p>
        </Section>

        {/* ── 2. Data We Collect ── */}
        <Section number="2" title="What Personal Data We Collect">
          <p>
            When you request a SafetyQR kit or register your QR sticker, we collect the
            following personal data:
          </p>
          <Table rows={[
            ["Full Name", "To identify you and address your kit"],
            ["Mobile Number", "To contact you regarding your kit and as your emergency contact identifier"],
            ["Area / Pincode", "To arrange kit delivery and verify serviceability"],
            ["Emergency Contact Details", "Name and phone number of your chosen emergency contact(s)"],
            ["Location Data", "Approximate location at the time a QR sticker is scanned in an emergency"],
            ["Device / QR Tag Identifier", "To link your profile to your physical sticker"],
            ["Quantity & Notes", "Optional — for institutional or bulk kit requests"],
          ]} />
          <p className="mt-4 text-xs text-muted-foreground">
            We do not collect financial information, Aadhaar numbers, biometric data,
            passwords, or browsing/behavioural data.
          </p>
        </Section>

        {/* ── 3. Why We Collect It ── */}
        <Section number="3" title="Why We Collect Your Data (Purpose)">
          <p>We collect and process your personal data for the following specific purposes:</p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li><strong>Emergency Response:</strong> To display your emergency contact information and relevant details to a first responder, good Samaritan, or medical personnel who scans your QR sticker during an emergency.</li>
            <li><strong>Kit Fulfillment:</strong> To process your kit request, arrange delivery, and follow up with you.</li>
            <li><strong>Service Communication:</strong> To send you confirmation and status updates about your kit via phone/email.</li>
            <li><strong>Platform Improvement:</strong> Aggregated, anonymised data may be used to improve the SafetyQR service.</li>
          </ul>
          <p className="mt-4">
            We do not use your personal data for advertising, profiling, or selling to
            third parties.
          </p>
        </Section>

        {/* ── 4. Legal Basis ── */}
        <Section number="4" title="Legal Basis for Processing (DPDPA 2023)">
          <p>
            Under the Digital Personal Data Protection Act, 2023 (India), we process your
            personal data on the following grounds:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li><strong>Consent (Section 6):</strong> You provide explicit consent when submitting a kit request or registering your sticker.</li>
            <li><strong>Legitimate Use — Medical Emergency (Section 7):</strong> When your QR sticker is scanned during a life-threatening emergency, processing your data to assist responders constitutes a legitimate use under the Act.</li>
          </ul>
        </Section>

        {/* ── 5. How Data Is Used ── */}
        <Section number="5" title="How Your Data Is Accessed">
          <p>Your data is accessed in the following limited scenarios:</p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li><strong>Emergency scan:</strong> When your QR sticker is scanned, your name, emergency contact, and an incident report form is shown to the scanner. Your full profile data is not publicly visible.</li>
            <li><strong>Kit delivery:</strong> Our team accesses your name, phone, and area to arrange and confirm kit delivery.</li>
            <li><strong>Support:</strong> Our support team may access your data to respond to your queries.</li>
          </ul>
        </Section>

        {/* ── 6. Third Parties ── */}
        <Section number="6" title="Third Parties We Share Data With">
          <p>
            We share data with the following trusted service providers only to the extent
            necessary for the above purposes:
          </p>
          <Table rows={[
            ["Resend (resend.com)", "Email delivery service — used to send kit request notifications to our team"],
            ["Neon (neon.tech)", "Cloud PostgreSQL database — stores your profile and kit registration data"],
            ["Vercel (vercel.com)", "Cloud hosting and deployment — serves the SafetyQR platform"],
            ["Cloudinary (cloudinary.com)", "Image hosting — if photos are uploaded as part of incident reports"],
          ]} />
          <p className="mt-4">
            We do not sell, rent, or trade your personal data with any third party for
            commercial purposes. All third-party processors are bound by appropriate data
            processing agreements.
          </p>
          <p className="mt-3">
            <strong>Cross-border transfers:</strong> Some of the above providers may store
            or process data outside India. As of the date of this policy, the Government of
            India has not blacklisted any countries under DPDPA Section 16, so such
            transfers are currently permissible.
          </p>
        </Section>

        {/* ── 7. Data Retention ── */}
        <Section number="7" title="How Long We Keep Your Data">
          <p>We retain your personal data for as long as:</p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li>Your QR sticker is actively registered with our platform, OR</li>
            <li>You have not withdrawn your consent, AND</li>
            <li>Retention is not required for legal compliance purposes.</li>
          </ul>
          <p className="mt-3">
            Upon account deletion or consent withdrawal, we will delete your personal data
            within a reasonable timeframe (and in any case within the period prescribed by
            DPDPA Rules 2025). You will receive a notification at least 48 hours before
            your data is permanently erased.
          </p>
          <p className="mt-3">
            Audit logs of data processing activities are retained for a minimum of one
            year for compliance purposes.
          </p>
        </Section>

        {/* ── 8. Children ── */}
        <Section number="8" title="Children's Data (Under 18 Years)">
          <div className="rounded-3xl border border-border/70 bg-muted/30 px-5 py-4 space-y-2">
            <p className="font-semibold">Students SafetyQR — Parental Consent Required</p>
            <p>
              If you are requesting a SafetyQR kit for a person under 18 years of age
              (including the Students SafetyQR kit), the registration <strong>must be
              completed by a parent or legal guardian</strong>.
            </p>
            <p>
              By submitting a kit request for a minor, you confirm that you are the
              parent or legal guardian of the child and that you consent to the collection
              and processing of their personal data as described in this Privacy Policy.
            </p>
            <p>
              We do not knowingly collect personal data directly from persons under 18
              without verifiable parental consent, in accordance with Section 9 of the
              DPDPA 2023.
            </p>
          </div>
        </Section>

        {/* ── 9. Your Rights ── */}
        <Section number="9" title="Your Rights Under DPDPA 2023">
          <p>
            As a Data Principal under the Digital Personal Data Protection Act, 2023, you
            have the following rights:
          </p>
          <Table rows={[
            ["Right to Access", "Request a summary of what personal data we hold about you and how it is being processed."],
            ["Right to Correction", "Request correction of inaccurate or incomplete personal data."],
            ["Right to Erasure", "Request deletion of your personal data (subject to legal retention requirements)."],
            ["Right to Data Portability", "Obtain your personal data in a structured, machine-readable format."],
            ["Right to Withdraw Consent", "Withdraw your consent at any time. Withdrawal is as easy as giving consent — contact us or delete your account."],
            ["Right to Nominate", "Nominate another person to exercise your rights on your behalf in the event of your death or incapacity."],
            ["Right to Grievance Redressal", "File a complaint about how we process your data and receive a response within 90 days."],
          ]} />
          <p className="mt-4">
            To exercise any of these rights, contact our Grievance Officer at{" "}
            <a href="mailto:support@feedbacknfc.com" className="text-primary hover:underline">
              support@feedbacknfc.com
            </a>. We will respond within 90 days.
          </p>
        </Section>

        {/* ── 10. Data Breach ── */}
        <Section number="10" title="Data Breach Notification">
          <p>
            In the event of a personal data breach, we will:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li>Notify the Data Protection Board of India without delay upon becoming aware of the breach.</li>
            <li>Submit a detailed breach report within 72 hours.</li>
            <li>Notify all affected users promptly, describing the nature of the breach, likely consequences, steps taken to mitigate it, and recommended protective actions.</li>
          </ul>
        </Section>

        {/* ── 11. Security ── */}
        <Section number="11" title="Security Measures">
          <p>
            We implement industry-standard technical and organisational measures to protect
            your personal data, including:
          </p>
          <ul className="mt-3 space-y-2 list-disc list-outside pl-5">
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Access controls limiting who can view personal data</li>
            <li>Regular security reviews of our infrastructure</li>
            <li>Secure cloud hosting via Vercel and Neon</li>
          </ul>
        </Section>

        {/* ── 12. Grievance Officer ── */}
        <Section number="12" title="Grievance Officer">
          <p>
            For any privacy-related complaints, queries, or to exercise your rights under
            DPDPA 2023, please contact our Grievance Officer:
          </p>
          <div className="mt-3 rounded-3xl border border-border/70 bg-muted/30 px-5 py-4 space-y-1 text-sm">
            <p><strong>Grievance Officer</strong></p>
            <p>FeedbackNFC / SafetyQR</p>
            <p>Email: <a href="mailto:support@feedbacknfc.com" className="text-primary hover:underline">support@feedbacknfc.com</a></p>
            <p>Response time: Within 90 days of receiving a request</p>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            If you are not satisfied with our response, you may file a complaint with the
            Data Protection Board of India once it becomes fully operational.
          </p>
        </Section>

        {/* ── 13. Updates ── */}
        <Section number="13" title="Updates to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will update
            the "Last updated" date at the top of this page. For significant changes, we
            will notify you via the contact details we hold for you. Your continued use of
            the SafetyQR service after any change constitutes your acceptance of the
            updated policy.
          </p>
        </Section>

        <Divider />

        {/* ── Footer nav ── */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <a href="/" className="text-primary hover:underline">← Back to SafetyQR</a>
          <span className="text-border">·</span>
          <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</a>
          <span className="text-border">·</span>
          <a href="/grievance" className="text-muted-foreground hover:text-foreground transition-colors">Tracked Support</a>
        </div>
      </div>
    </div>
  )
}

// ─── Local helpers ────────────────────────────────────────────────────────────

function Section({
  number,
  title,
  children,
}: {
  number: string
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-xl sm:text-2xl leading-snug">
        {number}. {title}
      </h2>
      <div className="text-sm leading-7 text-muted-foreground">{children}</div>
    </section>
  )
}

function Table({ rows }: { rows: [string, string][] }) {
  return (
    <div className="mt-3 overflow-hidden rounded-3xl border border-border/70">
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([label, value], i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-muted/20" : ""}>
              <td className="px-4 py-3 font-medium text-foreground align-top w-2/5 border-b border-border/50 last:border-0">
                {label}
              </td>
              <td className="px-4 py-3 text-muted-foreground align-top border-b border-border/50 last:border-0">
                {value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Divider() {
  return <hr className="border-border/50" />
}
